

<template>
   <div class="w-full" id="WhereYouLive">
      <ValidationObserver tag="form" ref="WhereYouLive" v-slot="observer">
         <h2 class="mb-5 text-2xl font-bold text-center lg:text-4xl">Where you live</h2>
         <div class="mt-5">
            <AggregatorEditRow :complete="isUKResidentFieldComplete" title="UK resident" :preview="`${prettyifyBooleanString(ukResidencyCode)}`">
               <ValidationProvider ref="ukResident" name="UK Resident selection" rules="required">
                  <div class="w-auto mx-auto lg:w-4/12" slot-scope="{ errors }">
                     <!-- Are you a UK Resident? -->
                     <p class="font-semibold label" for="residentialStatusCode">Are you a UK Resident?</p>
                     <!-- Yes -->
                     <input v-model="ukResidencyCode" type="radio" id="residentialStatusCodeYes" value="Y" />
                     <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="residentialStatusCodeYes">
                        Yes
                        <TickIcon class="tick-icon"/>
                     </label>
                     <!-- No -->
                     <input v-model="ukResidencyCode" type="radio" id="residentialStatusCodeNo" value="N" />
                     <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="residentialStatusCodeNo">
                        No
                        <TickIcon class="tick-icon"/>
                     </label>
                     <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
                  </div>
               </ValidationProvider>
            </AggregatorEditRow>
            <AggregatorEditRow :complete="isResidentialStatusFieldComplete" title="Residential status" :preview="`${prettifyResidentialStatus(residentialStatusCode)}`">
               <ValidationProvider ref="residentialStatusCode" name="Residential Status selection" rules="required">
                  <div slot-scope="{ errors }">
                     <!-- Residential Status -->
                     <fieldset>
                        <legend class="font-semibold label">Residential Status</legend>
                        <div class="inline" v-for="residentialStatus in residentialStatusCodes" :key="residentialStatus">
                           <input v-model="residentialStatusCode" type="radio" :id="lower(prettifyResidentialStatus(residentialStatus))" :value="residentialStatus" />
                           <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" :for="lower(prettifyResidentialStatus(residentialStatus))">
                              {{ prettifyResidentialStatus(residentialStatus) }}
                              <TickIcon class="tick-icon"/>
                           </label>
                        </div>
                     </fieldset>
                     <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
                  </div>
               </ValidationProvider>
            </AggregatorEditRow>
            <!-- Current Address -->
            <AggregatorEditRow :hide-button="shouldHideCompleteButtonForCompleteAddress" :complete="isCurrentAddressComplete" title="Current Address" :preview="`${prettifyAddress(currentAddress)}`">
               <div class="w-full lg:w-11/12 lg:ml-auto">
                  <h2 class="text-3xl font-semibold">Current address</h2>
                  <div v-if="toggles.showAddressEntry && currentAddress">
                     <div class="flex">
                        <div class="lg:w-3/4">
                           <h3 class="font-semibold">Address</h3>
                           <p> {{ prettifyAddress(currentAddress) }}
                           <h3 class="mt-4 font-semibold">Time at address</h3>
                           {{ currentAddress.yearsAtAddress }} years, {{ currentAddress.monthsAtAddress }} months
                        </div>
                        <div class="w-1/4 h-12">
                           <button type="button" @click="changeCurrentAddress(), sectionResetFocus()" class="block px-4 pt-0 py-2 underline">
                           Change
                           </button>
                           <button type="button" @click="toggles.changeCurrentTimeAtAddress = true, syncCurrentTimeAtAddressFromState()" class="block px-4 py-2 mt-12 md:mt-6 underline">
                           Change
                           </button>
                        </div>
                     </div>
                     <div v-if="toggles.changeCurrentTimeAtAddress">
                        <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
                        <div class="flex mt-4">
                           <div class="w-full md:w-4/12 mr-4 md:mr-0">
                              <label class="font-semibold label" for="years">Years</label>
                              <time-at-address :max="99" :model.sync="currentTimeAtAddress.years"/>
                           </div>
                           <div class="w-full md:w-4/12 ml-0 lg:ml-6">
                              <label class="font-semibold label" for="years">Months</label>
                              <time-at-address :max="11" :model.sync="currentTimeAtAddress.months"/>
                           </div>
                        </div>
                        <div :class="{ 'mb-4': toggles.previousAddressRequired }">
                           <button type="button" @click="saveCurrentTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow hover:bg-polred-dark bg-polred">
                           Save time at address
                           </button>
                           <button type="button" @click="cancelCurrentTimeAtAddress()" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
                           Cancel
                           </button>
                        </div>
                     </div>
                  </div>
                  <div v-else>
                     <AddressSearch :is-aggregator-flow="true" id="current"></AddressSearch>
                  </div>
               </div>
            </AggregatorEditRow>
            <AggregatorEditRow v-show="isCurrentAddressComplete" :complete="isCurrentAddressComplete" :hide-button="shouldHideCompleteButtonForCompleteAddress" title="Time at current address" :preview="prettifyTimeAtAddress(currentAddress)">
               <div class="w-full lg:w-11/12 lg:ml-auto">
                  <h2 class="text-3xl font-semibold">Current address</h2>
                  <div v-if="toggles.showAddressEntry && currentAddress">
                     <div class="flex">
                        <div class="lg:w-3/4">
                           <h3 class="font-semibold">Address</h3>
                           <p> {{ prettifyAddress(currentAddress) }}
                           <h3 class="mt-4 font-semibold">Time at address</h3>
                           {{ currentAddress.yearsAtAddress }} years, {{ currentAddress.monthsAtAddress }} months
                        </div>
                        <div class="w-1/4 h-12">
                           <button type="button" @click="changeCurrentAddress()" class="block px-4 pt-0 py-2 underline">
                           Change
                           </button>
                           <button type="button" @click="toggles.changeCurrentTimeAtAddress = true, syncCurrentTimeAtAddressFromState()" class="block px-4 py-2 mt-12 md:mt-6 underline">
                           Change
                           </button>
                        </div>
                     </div>
                     <div v-if="toggles.changeCurrentTimeAtAddress">
                        <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
                        <div class="flex mt-4">
                           <div class="w-full md:w-4/12 mr-4 md:mr-0">
                              <label class="font-semibold label" for="years">Years</label>
                              <time-at-address :max="99" :model.sync="currentTimeAtAddress.years"/>
                           </div>
                           <div class="w-full md:w-4/12 ml-0 lg:ml-6">
                              <label class="font-semibold label" for="years">Months</label>
                              <time-at-address :max="11" :model.sync="currentTimeAtAddress.months"/>
                           </div>
                        </div>
                        <div :class="{ 'mb-4': toggles.previousAddressRequired }">
                           <button type="button" @click="saveCurrentTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow hover:bg-polred-dark bg-polred">
                           Save time at address
                           </button>
                           <button type="button" @click="cancelCurrentTimeAtAddress()" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
                           Cancel
                           </button>
                        </div>
                     </div>
                  </div>
                  <div v-else>
                     <AddressSearch :is-aggregator-flow="true" id="current"></AddressSearch>
                  </div>
               </div>
            </AggregatorEditRow>
            <!-- Previous Address -->
            <AggregatorEditRow v-if="needToShowPreviousAddressFields" :hide-button="showPreviousAddressEdit" :complete="isPreviousAddressComplete" title="Previous Address" :preview="prettifyAddress(previousAddress)">
               <div class="w-full lg:w-11/12 lg:ml-auto">
                  <!-- Previous Address Required -->
                  <h2 class="mt-5 text-3xl font-semibold">Previous address</h2>
                  <p class="mb-4 text-m">Since you have lived at your current address less than two years, we need your previous address to complete your address history.</p>
                  <div v-if="previousAddress && !toggles.showPreviousAddressEntry">
                     <div class="flex mb-4">
                        <div class="w-3/4">
                           <h3 class="font-semibold">Address</h3>
                           <template v-for="(value, key, index) in previousAddress">
                              <span :key="index" v-if="!['yearsAtAddress', 'monthsAtAddress'].includes(key)">{{ value }}<span :key="index" v-if="index !== 3">, </span>
                              </span>
                           </template>
                           <h4 class="mt-4 font-semibold">Time at address</h4>
                           {{ previousAddress.yearsAtAddress }} years, {{ previousAddress.monthsAtAddress }} months
                        </div>
                        <div class="w-1/4 h-12">
                           <button type="button" @click="toggles.showPreviousAddressEntry = true" class="block px-4 py-2 underline">
                           Change
                           </button>
                           <button type="button" @click="changeTimeAtPreviousAddress()" class="block px-4 py-2 mt-12 md:mt-4 underline">
                           Change
                           </button>
                        </div>
                     </div>
                  </div>
                  <div v-if="currentAddress.yearsAtAddress < minAllowedYears && toggles.showPreviousAddressEntry">
                     <AddressSearch :is-aggregator-flow="true" id="previous" />
                  </div>
                  <div v-if="toggles.changePreviousTimeAtAddress">
                     <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
                     <div class="flex mt-4">
                        <div class="w-full md:w-4/12">
                           <label class="font-semibold label" for="years">Years</label>
                           <time-at-address :max="99" :model.sync="previousTimeAtAddress.years"/>
                        </div>
                        <div class="w-full md:w-4/12 ml-0 lg:ml-6">
                           <label class="font-semibold label" for="years">Months</label>
                           <time-at-address :min="previousTimeAtAddress.years > 0 ? 0 : 1" :max="11" :model.sync="previousTimeAtAddress.months"/>
                        </div>
                     </div>
                     <button type="button" @click="savePreviousTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
                     Save time at address
                     </button>
                     <button type="button" @click="toggles.changePreviousTimeAtAddress = false" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
                     Cancel
                     </button>
                  </div>
               </div>
            </AggregatorEditRow>
            <AggregatorEditRow v-if="needToShowPreviousAddressFields" :complete="isPreviousAddressComplete" title="Time at previous address" :preview="prettifyTimeAtAddress(previousAddress)">
               <div class="w-full lg:w-11/12 lg:ml-auto">
                  <!-- Previous Address Required -->
                  <h2 class="mt-5 text-3xl font-semibold">Previous address</h2>
                  <p class="mb-4 text-m">Since you have lived at your current address less than two years, we need your previous address to complete your address history.</p>
                  <div v-if="previousAddress && !toggles.showPreviousAddressEntry">
                     <div class="flex mb-4">
                        <div class="w-3/4">
                           <h3 class="font-semibold">Address</h3>
                           <template v-for="(value, key, index) in previousAddress">
                              <span :key="index" v-if="!['yearsAtAddress', 'monthsAtAddress'].includes(key)">{{ value }}<span :key="index" v-if="index !== 3">, </span>
                              </span>
                           </template>
                           <h3 class="mt-4 font-semibold">Time at address</h3>
                           {{ previousAddress.yearsAtAddress }} years, {{ previousAddress.monthsAtAddress }} months
                        </div>
                        <div class="w-1/4 h-12">
                           <button type="button" @click="toggles.showPreviousAddressEntry = true" class="block px-4 py-2 underline">
                           Change
                           </button>
                           <button type="button" @click="changeTimeAtPreviousAddress()" class="block px-4 py-2 mt-12 md:mt-4 underline">
                           Change
                           </button>
                        </div>
                     </div>
                  </div>
                  <div v-if="currentAddress.yearsAtAddress < minAllowedYears && toggles.showPreviousAddressEntry">
                     <AddressSearch :is-aggregator-flow="true" id="previous" />
                  </div>
                  <div v-if="toggles.changePreviousTimeAtAddress">
                     <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
                     <div class="flex mt-4">
                        <div class="w-full md:w-4/12">
                           <label class="font-semibold label" for="years">Years</label>
                           <time-at-address :max="99" :model.sync="previousTimeAtAddress.years"/>
                        </div>
                        <div class="w-full md:w-4/12 ml-0 lg:ml-6">
                           <label class="font-semibold label" for="years">Months</label>
                           <time-at-address :min="previousTimeAtAddress.years > 0 ? 0 : 1" :max="11" :model.sync="previousTimeAtAddress.months"/>
                        </div>
                     </div>
                     <button type="button" @click="savePreviousTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
                     Save time at address
                     </button>
                     <button type="button" @click="toggles.changePreviousTimeAtAddress = false" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
                     Cancel
                     </button>
                  </div>
               </div>
            </AggregatorEditRow>
         </div>
      </ValidationObserver>
   </div>
</template>

<script>

import TickIcon from '@/v2/assets/images/icons/select-tick.svg'

import { mapFields } from 'vuex-map-fields'

import TimeAtAddress from '@/v2/components/elements/TimeAtAddress'
import AddressSearch from '@/v2/components/elements/AddressSearch'
import AggregatorEditRow from '@/v2/components/elements/AggregatorFlow/EditRow'

import mixins from '@/v2/mixins'
import { residentialStatusCodes } from '../../../form'

export default {
  name: 'WhereYouLive',
  mixins: [mixins],
  data() {
    return {
      residentialStatusCodes,
      toggles: {
        showAddressEntry: false,
        showPreviousAddressEntry: false,
        previousAddressRequired: false,
        changePreviousTimeAtAddress: false,
        changeCurrentTimeAtAddress: false,
        hideCurrentAddress: false,
      },
      isUKResidentFieldComplete: true,
      isResidentialStatusFieldComplete: true,
      isAddressComplete: true,
      currentTimeAtAddress: {
        years: 0,
        months: 0
      },
      previousTimeAtAddress: {
        years: 0,
        months: 0
      },
      minAllowedYears: parseInt(process.env.VUE_APP_ADDRESS_MIN_YEARS),
    }
  },
   methods: {
      sectionResetFocus() {
         let focusChangeAddress = function(){
            if(document.getElementById("AddressSearch")){
               document.getElementById("AddressSearch").focus();
            }
            else {
               setTimeout(focusChangeAddress, 100);
            }
         }
         focusChangeAddress();
      },
   },
  computed : {
    ...mapFields([
      'person.residentialStatusCode',
      'person.ukResidencyCode',
      'person.currentAddress',
      'person.previousAddress',
    ]),
    shouldHideCompleteButtonForCompleteAddress() {
      return !this.currentAddress || this.isset(this.currentAddress.yearsAtAddress) && (this.currentAddress.yearsAtAddress < this.minAllowedYears && !this.previousAddress)
    },
    showPreviousAddressEdit() {
      return this.currentAddress && this.currentAddress.yearsAtAddress < parseInt(process.env.VUE_APP_ADDRESS_MIN_YEARS) && !this.previousAddress
    },
    isSectionComplete() {
      return this.isUKResidentFieldComplete, this.isResidentialStatusFieldComplete, this.isAddressComplete, Date.now()
    },
    ukResidentField() {
      return this.ukResidencyCode, Date.now()
    },
    residentialStatusField() {
      return this.residentialStatusCode, Date.now()
    },
    addressField() {
      return this.currentAddress, this.previousAddress, Date.now()
    },
    isCurrentAddressComplete() {
      return this.currentAddress && this.currentAddress.postCode && this.isset(this.currentAddress.yearsAtAddress) && this.isset(this.currentAddress.monthsAtAddress)
    },
    isPreviousAddressComplete() {
      return this.isCurrentAddressComplete && (this.currentAddress.yearsAtAddress < this.minAllowedYears && this.previousAddress && this.previousAddress.postCode && this.isset(this.previousAddress.yearsAtAddress) && this.isset(this.previousAddress.monthsAtAddress))
    },
    needToShowPreviousAddressFields() {
      return (this.currentAddress && this.currentAddress.yearsAtAddress < this.minAllowedYears)
    }
  },
  watch: {
    isSectionComplete: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          await this.$root.$emit(`whereYouLiveComplete`, this.isResidentialStatusFieldComplete && this.isAddressComplete && this.isUKResidentFieldComplete)
        })
      },
    },
    ukResidentField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isUKResidentFieldComplete = await this.validateField(this.$refs, 'ukResident')
        })
      }
    },
    residentialStatusField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isResidentialStatusFieldComplete = await this.validateField(this.$refs, 'residentialStatusCode')
        })
      }
    },
    addressField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {

          let status = true

          if (!this.isset(this.currentAddress)) {
            status = false
          }

          if (this.isset(this.currentAddress) && this.currentAddress.yearsAtAddress < parseInt(process.env.VUE_APP_ADDRESS_MIN_YEARS) && !this.previousAddress) {
            this.toggles.previousAddressRequired = true
            status = false
          }

          if (this.isset(this.currentAddress) && !this.currentAddress.postCode) {
            status = false
          }

          if (this.needToShowPreviousAddressFields && !this.isPreviousAddressComplete) {
            status = false
          }

          if (this.isset(this.currentAddress) && this.currentAddress.yearsAtAddress < parseInt(process.env.VUE_APP_ADDRESS_MIN_YEARS) && !this.isPreviousAddressComplete) {
            this.toggles.previousAddressRequired = true
            this.toggles.showPreviousAddressEntry = true
          } else if (this.isPreviousAddressComplete) {
            this.toggles.previousAddressRequired = true
          } else {
            this.toggles.previousAddressRequired = false
            this.toggles.showPreviousAddressEntry = false
          }

          this.isAddressComplete = status
        })
      }
    },
  },
  components: {
    AggregatorEditRow,
    TimeAtAddress,
    AddressSearch,
    TickIcon
  },
  
  mounted() {
    
    this.$root.$on('AddressSearch:set', (address) => {
      this.setSelectedAddress(address)
    })

    if (this.currentAddress && this.currentAddress.yearsAtAddress !== null && this.currentAddress.monthsAtAddress !== null) {
      this.currentTimeAtAddress.years = this.currentAddress.yearsAtAddress
      this.currentTimeAtAddress.months = this.currentAddress.monthsAtAddress
    }

    if (this.previousAddress && this.previousAddress.yearsAtAddress !== null && this.previousAddress.monthsAtAddress !== null) {
      this.previousTimeAtAddress.years = this.previousAddress.yearsAtAddress
      this.previousTimeAtAddress.months = this.previousAddress.monthsAtAddress
    }

    this.toggles.showAddressEntry = true
  }
}
</script>
